import classNames from "classnames";
import * as React from "react";

import styles from "./Card.module.css";

/**
 * How much header a card carries. `section` is a page section: a heading-sized
 * title with a description under it. `compact` is one item in a list: a
 * small-caps label on a tinted strip, with a one-line summary beside it.
 */
export type CardSize = "section" | "compact";

export type CardRootProps = React.ComponentPropsWithoutRef<"div"> & {
  size?: CardSize;
};
export type CardPartProps = React.ComponentPropsWithoutRef<"div">;
export type CardTextProps = React.HTMLAttributes<HTMLElement>;

const CardSizeContext = React.createContext<CardSize>("section");

/** The bordered box. */
function Root({ size = "section", className, ...props }: CardRootProps) {
  return (
    <CardSizeContext.Provider value={size}>
      <div
        className={classNames(styles.root, className)}
        data-size={size}
        {...props}
      />
    </CardSizeContext.Provider>
  );
}

/**
 * The strip across the top, ruled off from the body: a title, an optional
 * description or summary, and actions pushed to the far end.
 */
function Header({ className, ...props }: CardPartProps) {
  return <div className={classNames(styles.header, className)} {...props} />;
}

/**
 * What the card is. A section card's title is an `h2`, so a stack of sections
 * reads as an outline; a compact card's is a small-caps label ("If", "Target").
 */
function Title({ className, ...props }: CardTextProps) {
  const Tag = React.useContext(CardSizeContext) === "section" ? "h2" : "span";
  return <Tag className={classNames(styles.title, className)} {...props} />;
}

/** What a section card is for, under its title. */
function Description({ className, ...props }: CardTextProps) {
  return <p className={classNames(styles.description, className)} {...props} />;
}

/** One line restating a compact card's contents; truncates rather than wraps. */
function Summary({ className, ...props }: CardTextProps) {
  return <span className={classNames(styles.summary, className)} {...props} />;
}

/** Controls for the whole card (remove, collapse), at the header's end. */
function Actions({ className, ...props }: CardPartProps) {
  return <div className={classNames(styles.actions, className)} {...props} />;
}

/** The card's contents, under the header. */
function Body({ className, ...props }: CardPartProps) {
  return <div className={classNames(styles.body, className)} {...props} />;
}

/**
 * A bordered card with a header strip. `Card` is itself `Card.Root`, the way
 * `Field` is its own root.
 *
 * @example
 * ```tsx
 * <Card.Root>
 *   <Card.Header>
 *     <Card.Title>This trigger</Card.Title>
 *     <Card.Description>The trigger as it is written.</Card.Description>
 *   </Card.Header>
 *   <Card.Body>…</Card.Body>
 * </Card.Root>
 *
 * <Card.Root size="compact">
 *   <Card.Header>
 *     <Card.Title>If</Card.Title>
 *     <Card.Summary>file.path contains field-test</Card.Summary>
 *     <Card.Actions>
 *       <RobotoButton variant="ghost" aria-label="Remove condition" … />
 *     </Card.Actions>
 *   </Card.Header>
 *   <Card.Body>…</Card.Body>
 * </Card.Root>
 * ```
 */
export const Card = Object.assign(Root, {
  Root,
  Header,
  Title,
  Description,
  Summary,
  Actions,
  Body,
});
